/** Every cue binding the live scenes declare, and which canvas kinds each one reaches. */

import { CopyField, Stack, StatusBadge } from "@/components/ui";

import { CanvasKindChip } from "../../components/Chips";
import { Mono, Muted } from "../../components/Text";
import type { SpaceActivities } from "../../spaces/lib/queries";

import { cueRows, type NamedScene } from "../lib/scenes";

import { LiveMounts, type MountReading } from "./LiveMounts";
import { Nothing, Section } from "./Section";

function Declared({ readings }: { readonly readings: readonly MountReading[] }) {
  const scenes: NamedScene[] = readings.flatMap((one) =>
    one.scenes.map((document) => ({ from: one.name, document })),
  );
  const rows = cueRows(scenes);
  const failed = readings.filter((one) => one.error !== null);

  return (
    <Stack gap={4}>
      {failed.map((one) => (
        <StatusBadge key={one.mountId} tone="danger">
          {`${one.name}: ${one.error ?? ""}`}
        </StatusBadge>
      ))}

      {rows.length === 0 ? (
        <Nothing headline="No cue binding declared" />
      ) : (
        rows.map((row) => (
          <Section key={row.id} title={row.tagPattern}>
            <Stack gap={2}>
              <CopyField label="tag" value={row.tagPattern} />
              <Muted small>
                <Mono>{`${row.from}, node ${row.nodeId}, ${row.presentation}`}</Mono>
              </Muted>
              {!row.bound ? (
                <StatusBadge tone="danger">no node with that id</StatusBadge>
              ) : row.canvases.length === 0 ? (
                <StatusBadge tone="warning">reaches nobody</StatusBadge>
              ) : (
                <div className="cx-chips">
                  {row.canvases.map((kind) => (
                    <CanvasKindChip key={kind} kind={kind} />
                  ))}
                </div>
              )}
            </Stack>
          </Section>
        ))
      )}
    </Stack>
  );
}

export function CuesPanel({ activities }: { readonly activities: SpaceActivities }) {
  const live = activities.rows.filter((one) => one.mount?.mount_id != null);

  if (live.length === 0) {
    return <Nothing headline="Nothing running" />;
  }

  return <LiveMounts rows={live}>{(readings) => <Declared readings={readings} />}</LiveMounts>;
}
